// Environment Configuration Manager
// This file manages all environment-specific configurations

export type Environment = 'local' | 'dev' | 'prod';

export interface EnvironmentConfig {
  oktaIssuer: string;
  oktaClientId: string;
  oktaRedirectUri: string;
  oktaPostLogoutRedirectUri: string;
  oktaScopes: string[];
  apiBaseUrl?: string;
  enableDebugLogging?: boolean;
}

// Get current environment
const getCurrentEnvironment = (): Environment => {
  const hostname = window.location.hostname;

  // Check if we're running locally
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }

  // Check for dev environment (explicit host allowlist)
  const devHosts = ['mireviq-dev.spglobal.com'];
  if (devHosts.includes(hostname)) {
    return 'dev';
  }

  // Production environment (explicit host allowlist)
  const prodHosts = ['mireviq.spglobal.com'];
  if (prodHosts.includes(hostname)) {
    return 'prod';
  }

  // Default to local for unknown environments
  return 'local';
};

// Helper to check if we're in local environment
export const isLocalEnvironment = (): boolean => {
  return getCurrentEnvironment() === 'local';
};

// Environment-specific configurations
const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  local: {
    oktaIssuer: process.env.REACT_APP_OKTA_ISSUER || 'https://spglobal.okta.com/oauth2/default',
    // Use dev client ID for local development (shared with dev environment)
    oktaClientId: process.env.REACT_APP_OKTA_CLIENT_ID || '0oasmgdadaQOO9hAl5d7',
    oktaRedirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI || 'http://localhost:3000/login/callback',
    oktaPostLogoutRedirectUri: process.env.REACT_APP_OKTA_POST_LOGOUT_REDIRECT_URI || 'http://localhost:3000',
    oktaScopes: ['openid', 'profile', 'email', 'groups'],
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api',
    enableDebugLogging: true,
  },
  dev: {
    oktaIssuer: process.env.REACT_APP_OKTA_ISSUER || 'https://spglobal.okta.com/oauth2/default',
    oktaClientId: process.env.REACT_APP_OKTA_CLIENT_ID || '0oasmgdadaQOO9hAl5d7',
    oktaRedirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI || 'https://mireviq-dev.spglobal.com/login/callback',
    oktaPostLogoutRedirectUri: process.env.REACT_APP_OKTA_POST_LOGOUT_REDIRECT_URI || 'https://mireviq-dev.spglobal.com',
    oktaScopes: ['openid', 'profile', 'email', 'groups'],
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'https://api-dev.spglobal.com/api',
    enableDebugLogging: true,
  },
  prod: {
    oktaIssuer: process.env.REACT_APP_OKTA_ISSUER || 'https://spglobal.okta.com/oauth2/default',
    oktaClientId: process.env.REACT_APP_OKTA_CLIENT_ID || '0oasmggntm7Aggd5s5d7',
    oktaRedirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI || 'https://mireviq.spglobal.com/login/callback',
    oktaPostLogoutRedirectUri: process.env.REACT_APP_OKTA_POST_LOGOUT_REDIRECT_URI || 'https://mireviq.spglobal.com',
    oktaScopes: ['openid', 'profile', 'email', 'groups'],
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'https://api.spglobal.com/api',
    enableDebugLogging: false,
  },
};

// Get configuration for current environment
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getCurrentEnvironment();
  const config = environmentConfigs[env];
  
  if (config.enableDebugLogging) {
    console.log(`🔧 Environment: ${env}`, config);
  }
  
  return config;
};

// Get current environment name
export const getEnvironment = (): Environment => {
  return getCurrentEnvironment();
};

// Check if running in local development mode
export const isDevelopment = (): boolean => {
  return getCurrentEnvironment() === 'local';
};

// Check if running in dev mode
export const isDev = (): boolean => {
  return getCurrentEnvironment() === 'dev';
};

// Check if running in production mode
export const isProduction = (): boolean => {
  return getCurrentEnvironment() === 'prod';
};

// Export the current config as default
export default getEnvironmentConfig();